import useVisibleProjects from "../hooks/useVisibleProjects";
// src/pages/ProjectOverviewPage.js
import React, { useEffect, useMemo, useState } from "react";
import LoadingIndicator from "../components/LoadingIndicator";
import { FaChevronDown } from "react-icons/fa";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";

// Stilkonstanter
const boxStyle = {
  position: "relative",
  display: "inline-block",
  flex: "0 1 220px",
  minWidth: 150,
  maxWidth: 260,
};

const inputStyle = {
  width: "100%",
  height: 50,
  padding: "8px 34px 8px 12px",
  border: "1px solid #d1d5db",
  borderRadius: 3,
  background: "#ffffff",
  fontSize: 14,
  color: "#111827",
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle = {
  position: "absolute",
  top: -10,
  left: 12,
  fontSize: "0.7rem",
  background: "#ffffff",
  padding: "0 4px",
  color: "#6b7280",
  userSelect: "none",
};

const toolbarButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 42,
  padding: "0 16px",
  borderRadius: 8,
  color: "white",
  fontSize: 14,
  fontWeight: 700,
  boxSizing: "border-box",
  cursor: "pointer",
  border: "none",
};

// Hjälpfunktion för att säkert tolka datumvärden
function safeParseDate(dateValue) {
  if (!dateValue) return null;

  try {
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }

    if (
      dateValue &&
      typeof dateValue === "object" &&
      typeof dateValue.toDate === "function"
    ) {
      const parsed = dateValue.toDate();
      return parsed instanceof Date && !isNaN(parsed.getTime()) ? parsed : null;
    }

    if (typeof dateValue === "string") {
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date;
    }

    if (typeof dateValue === "number") {
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date;
    }

    return null;
  } catch (error) {
    console.error("Fel vid parsning av datum:", error);
    return null;
  }
}

// Hjälpfunktion för att konvertera tid till minuter
function parseTimeToMinutes(value) {
  if (!value || typeof value !== "string") return null;

  const trimmed = value.trim();
  const match = trimmed.match(/^(\d{1,2})[:.](\d{2})$/);

  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);

  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  return hours * 60 + minutes;
}

// Hjälpfunktion för att avrunda till 2 decimaler
function roundTo2(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

// Hjälpfunktion för att formatera timmar
function formatHours(value, withSuffix = false) {
  const num = roundTo2(value);
  return withSuffix ? `${num.toFixed(2)} h` : num.toFixed(2);
}

// Hjälpfunktion för att normalisera projektnamn
function normalizeProjectName(value) {
  return (value || "").trim() || "Projekt saknas";
}

// Beräknar totala timmar för ett pass
function calculateShiftHours(shift) {
  if (!shift) {
    return 0;
  }

  // Kolla om totalHours finns explicit
  const explicitTotal = Number(
    shift.totalHours ?? shift.hours ?? shift.durationHours ?? 0
  );

  if (!Number.isNaN(explicitTotal) && explicitTotal > 0) {
    return roundTo2(explicitTotal);
  }

  // Beräkna från start- och sluttid
  const startMinutes = parseTimeToMinutes(shift.startTime);
  const endMinutes = parseTimeToMinutes(shift.endTime);

  if (startMinutes !== null && endMinutes !== null) {
    let diff = endMinutes - startMinutes;

    if (diff < 0) {
      diff += 24 * 60;
    }

    const unpaidBreakMinutes = Number(
      shift.breakMinutes ?? shift.unpaidBreakMinutes ?? 0
    );
    const breakHours = Number(shift.breakHours ?? 0);

    const totalHoursFromTime = Math.max(
      0,
      diff / 60 - unpaidBreakMinutes / 60 - breakHours
    );

    return roundTo2(totalHoursFromTime);
  }

  return 0;
}

// Hjälpfunktion för att hämta kundnamn för ett projekt
function getCustomerNameForProject(projectName, projects, customers) {
  const normalizedProjectName = normalizeProjectName(projectName).toLowerCase();

  const projectMatch = projects.find(
    (project) => (project.name || "").trim().toLowerCase() === normalizedProjectName
  );

  if (!projectMatch) return "Ingen kund";

  if (projectMatch.customerName && String(projectMatch.customerName).trim()) {
    return String(projectMatch.customerName).trim();
  }

  const customerRef =
    projectMatch.customerId ||
    projectMatch.customerRef ||
    projectMatch.customer ||
    "";

  if (!customerRef) return "Ingen kund";

  const customerById = customers.find((customer) => customer._id === customerRef);
  if (customerById?.name) return customerById.name;

  const customerByName = customers.find(
    (customer) =>
      (customer.name || "").trim().toLowerCase() ===
      String(customerRef).trim().toLowerCase()
  );

  if (customerByName?.name) return customerByName.name;

  return "Ingen kund";
}

// Huvudkomponent för projektöversikt
export default function ProjectOverviewPage() {
  const { currentUser, profile } = useAuth();
  const { activeCompany, loading: companyLoading } = useCompany();

  // State för filtrering och sökning
  const [searchTerm, setSearchTerm] = useState("");
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [monthFilter, setMonthFilter] = useState(
    new Date().toLocaleString("sv-SE", { month: "long" }).replace(/^./, (c) => c.toUpperCase())
  );
  const [schemaFilter, setSchemaFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");

  // State för data
  const [schemas, setSchemas] = useState([]);
  const [allProjects, setProjects] = useState([]);
  const projects = useVisibleProjects(allProjects);
  const [customers, setCustomers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  // Hämta companyAccountUid från activeCompany
  const companyAccountUid = activeCompany?.companyAccountUid;

  // Hämta anställda - använder companyAccountUid
  useEffect(() => {
    if (!companyAccountUid) {
      setEmployees([]);
      return;
    }

    const employedRef = collection(db, "users", companyAccountUid, "employed");

    const unsubscribe = onSnapshot(
      employedRef,
      (snapshot) => {
        const users = [];

        snapshot.forEach((userDoc) => {
          const userData = userDoc.data();

          let fullName = userData.name || "";
          if (userData.firstName && userData.lastName) {
            fullName = `${userData.firstName} ${userData.lastName}`;
          } else if (userData.firstName) {
            fullName = userData.firstName;
          } else if (userData.lastName) {
            fullName = userData.lastName;
          } else if (userData.displayName) {
            fullName = userData.displayName;
          }

          users.push({
            _id: userDoc.id,
            id: userDoc.id,
            ...userData,
            name: fullName || "Användare",
            firstName: userData.firstName || "",
            lastName: userData.lastName || "",
          });
        });

        users.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setEmployees(users);
      },
      (error) => {
        console.error("Fel vid hämtning av anställda:", error);
      }
    );

    return () => unsubscribe();
  }, [companyAccountUid]);

  // Hämta scheman - använder companyAccountUid
  useEffect(() => {
    if (!companyAccountUid) {
      setSchemas([]);
      setProjects([]);
      setCustomers([]);
      setShifts([]);
      setLoading(false);
      return;
    }

    const schemasRef = collection(db, "users", companyAccountUid, "Scheduling");
    const unsubscribe = onSnapshot(
      schemasRef,
      (snapshot) => {
        const schemaList = [];
        snapshot.forEach((schemaDoc) => {
          const data = schemaDoc.data();
          schemaList.push({
            _id: schemaDoc.id,
            name: data.name || "Namnlöst schema",
          });
        });

        schemaList.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setSchemas(schemaList);
      },
      (error) => {
        console.error("Fel vid hämtning av scheman:", error);
      }
    );

    return () => unsubscribe();
  }, [companyAccountUid]);

  // Hämta projekt - använder companyAccountUid
  useEffect(() => {
    if (!companyAccountUid) return;

    const projectsRef = collection(db, "users", companyAccountUid, "projects");
    const unsubscribe = onSnapshot(
      projectsRef,
      (snapshot) => {
        const projectList = [];

        snapshot.forEach((projectDoc) => {
          const data = projectDoc.data();

          projectList.push({
            _id: projectDoc.id,
            name: data.name || "Projekt saknas",
            area: data.area || "",
            customerId: data.customerId || "",
            customerName: data.customerName || "",
            description: data.description || "",
          });
        });

        projectList.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setProjects(projectList);
      },
      (error) => {
        console.error("Fel vid hämtning av projekt:", error);
      }
    );

    return () => unsubscribe();
  }, [companyAccountUid]);

  // Hämta kunder - använder companyAccountUid
  useEffect(() => {
    if (!companyAccountUid) return;

    const customersRef = collection(db, "users", companyAccountUid, "customers");
    const unsubscribe = onSnapshot(
      customersRef,
      (snapshot) => {
        const customerList = [];

        snapshot.forEach((customerDoc) => {
          const data = customerDoc.data();
          customerList.push({
            _id: customerDoc.id,
            name: data.name || "Kund saknas",
          });
        });

        customerList.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setCustomers(customerList);
      },
      (error) => {
        console.error("Fel vid hämtning av kunder:", error);
      }
    );

    return () => unsubscribe();
  }, [companyAccountUid]);

  // Hämta pass - använder companyAccountUid
  useEffect(() => {
    if (!companyAccountUid) return;

    setLoading(true);

    const shiftsRef = collection(db, "users", companyAccountUid, "shifts");
    const unsubscribe = onSnapshot(
      shiftsRef,
      (snapshot) => {
        const shiftList = [];

        snapshot.forEach((shiftDoc) => {
          shiftList.push({
            _id: shiftDoc.id,
            ...shiftDoc.data(),
          });
        });

        setShifts(shiftList);
        setLoading(false);
      },
      (error) => {
        console.error("Fel vid hämtning av pass:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyAccountUid]);

  // Tillgängliga år baserat på pass
  const availableYears = useMemo(() => {
    const years = new Set();

    shifts.forEach((shift) => {
      const parsed = safeParseDate(shift.date);
      if (parsed) {
        years.add(String(parsed.getFullYear()));
      }
    });

    if (!years.size) {
      years.add(String(new Date().getFullYear()));
    }

    return Array.from(years).sort((a, b) => Number(a) - Number(b));
  }, [shifts]);

  // Månadsalternativ
  const monthOptions = [
    "Januari",
    "Februari",
    "Mars",
    "April",
    "Maj",
    "Juni",
    "Juli",
    "Augusti",
    "September",
    "Oktober",
    "November",
    "December",
  ];

  const monthNameToIndex = {
    Januari: 0,
    Februari: 1,
    Mars: 2,
    April: 3,
    Maj: 4,
    Juni: 5,
    Juli: 6,
    Augusti: 7,
    September: 8,
    Oktober: 9,
    November: 10,
    December: 11,
  };

  const customerOptions = useMemo(() => customers, [customers]);
  const schemaOptions = useMemo(() => schemas, [schemas]);

  // Skapa en map för att snabbt kunna slå upp anställdas namn
  const employeeMap = useMemo(() => {
    const map = {};
    employees.forEach((emp) => {
      map[emp._id || emp.id] = emp.name;
    });
    return map;
  }, [employees]);

  // Expandera shifts för att inkludera alla anställda som är kopplade till varje pass
  const expandedShifts = useMemo(() => {
    const expanded = [];
    
    shifts.forEach((shift) => {
      // Om passet har en employees-array (ny struktur)
      if (shift.employees && Array.isArray(shift.employees)) {
        shift.employees.forEach((employeeData, index) => {
          if (employeeData.employeeId) {
            const employee = employees.find(emp => (emp._id || emp.id) === employeeData.employeeId);
            
            expanded.push({
              ...shift,
              id: `${shift._id}-${index}`,
              originalId: shift._id,
              participantIndex: index,
              employeeId: employeeData.employeeId,
              employee: employee?.name || "Okänd",
              employeeName: employee?.name || "Okänd",
            });
          }
        });
      } 
      // Gammal struktur med enstaka employee
      else if (shift.employeeId || shift.employee) {
        expanded.push({
          ...shift,
          id: `${shift._id}-main`,
          originalId: shift._id,
          participantIndex: 0,
          employeeId: shift.employeeId,
          employee: shift.employee || employeeMap[shift.employeeId] || "Okänd",
          employeeName: shift.employee || employeeMap[shift.employeeId] || "Okänd",
        });
      }
    });

    return expanded;
  }, [shifts, employees, employeeMap]);

  // Filtrera och gruppera pass per projekt
  const filteredRows = useMemo(() => {
    const projectCustomerMap = new Map();

    projects.forEach((project) => {
      projectCustomerMap.set(normalizeProjectName(project.name), {
        customerId: project.customerId || "",
        customerName: getCustomerNameForProject(project.name, projects, customers),
      });
    });

    const grouped = new Map();

    expandedShifts.forEach((shift) => {
      const shiftDate = safeParseDate(shift.date);
      if (!shiftDate) return;

      const shiftYear = String(shiftDate.getFullYear());
      const shiftMonth = shiftDate.getMonth();

      if (yearFilter && shiftYear !== yearFilter) return;
      if (
        monthFilter &&
        monthNameToIndex[monthFilter] !== undefined &&
        shiftMonth !== monthNameToIndex[monthFilter]
      ) {
        return;
      }

      const shiftSchemaId = shift.schemaId || shift.schema || "";
      if (schemaFilter && shiftSchemaId !== schemaFilter) return;

      const projectName = normalizeProjectName(shift.project);
      const customerInfo = projectCustomerMap.get(projectName) || {
        customerId: "",
        customerName: "Ingen kund",
      };

      if (customerFilter && customerInfo.customerId !== customerFilter) return;

      const searchableText = [
        projectName,
        customerInfo.customerName,
      ]
        .join(" ")
        .toLowerCase();

      if (searchTerm && !searchableText.includes(searchTerm.toLowerCase())) return;

      const hours = calculateShiftHours(shift);

      if (!grouped.has(projectName)) {
        grouped.set(projectName, {
          project: projectName,
          totalHours: 0,
          shiftsCount: 0,
          customerName: customerInfo.customerName || "Ingen kund",
        });
      }

      const row = grouped.get(projectName);
      row.totalHours += hours;
      row.shiftsCount += 1;
    });

    const rows = Array.from(grouped.values()).map((row) => ({
      ...row,
      totalHours: roundTo2(row.totalHours),
    }));

    rows.sort((a, b) => (a.project || "").localeCompare(b.project || ""));
    return rows;
  }, [
    expandedShifts,
    projects,
    customers,
    searchTerm,
    yearFilter,
    monthFilter,
    schemaFilter,
    customerFilter,
  ]);

  // Summeringar för totaler
  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, row) => {
        acc.totalShifts += row.shiftsCount;
        acc.totalHours += row.totalHours;
        return acc;
      },
      {
        totalShifts: 0,
        totalHours: 0,
      }
    );
  }, [filteredRows]);

  // Exportera data till CSV
  const handleExport = () => {
    const headers = [
      "Projekt",
      "Totalt (h)",
      "Antal pass",
      "Kund",
    ];

    const csvRows = filteredRows.map((row) => [
      row.project,
      formatHours(row.totalHours),
      row.shiftsCount,
      row.customerName || "Ingen kund",
    ]);

    const csvContent = [headers.join(";"), ...csvRows.map((row) => row.join(";"))].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "projektoversikt.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Visa laddningsindikator medan företag laddas
  if (companyLoading || loading) {
    return <LoadingIndicator message="Laddar projektöversikt..." fullPage />;
  }

  // Visa meddelande om inget företag är valt
  if (!activeCompany) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}>
        <p>Inget företag valt. Vänligen logga in igen.</p>
        <button
          onClick={() => window.location.href = "/login"}
          style={{
            padding: "10px 20px",
            background: "#6323ef",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
            marginTop: 16,
          }}
        >
          Gå till inloggning
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: "0 0 24px" }}>
      {/* Visa aktivt företag */}
      <div style={{ marginBottom: 16, padding: "0 0 8px" }}>
        <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>
          Visar projektöversikt för: <strong>{activeCompany?.companyName}</strong>
        </p>
      </div>

      {/* Verktygsrad med filter */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 0 20px",
          overflowX: "auto",
          overflowY: "hidden",
          whiteSpace: "nowrap",
          boxSizing: "border-box",
          flexWrap: "nowrap",
        }}
      >
        {/* Sökfält */}
        <div style={{ ...boxStyle, minWidth: 320, maxWidth: 420, flex: "1 1 360px" }}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Sök på projekt eller kund..."
            style={inputStyle}
          />
        </div>

        {/* Schema-filter */}
        <div style={boxStyle}>
          <select
            value={schemaFilter}
            onChange={(e) => setSchemaFilter(e.target.value)}
            style={{
              ...inputStyle,
              appearance: "none",
              MozAppearance: "none",
              WebkitAppearance: "none",
            }}
          >
            <option value="">Alla scheman</option>
            {schemaOptions.map((schema) => (
              <option key={schema._id} value={schema._id}>
                {schema.name}
              </option>
            ))}
          </select>

          <FaChevronDown
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: 12,
              color: "#6b7280",
            }}
          />
          <span style={labelStyle}>Schema</span>
        </div>

        {/* Kund-filter */}
        <div style={boxStyle}>
          <select
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            style={{
              ...inputStyle,
              appearance: "none",
              MozAppearance: "none",
              WebkitAppearance: "none",
            }}
          >
            <option value="">Alla kunder</option>
            {customerOptions.map((customer) => (
              <option key={customer._id} value={customer._id}>
                {customer.name}
              </option>
            ))}
          </select>

          <FaChevronDown
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: 12,
              color: "#6b7280",
            }}
          />
          <span style={labelStyle}>Kund</span>
        </div>

        {/* År-filter */}
        <div style={boxStyle}>
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            style={{
              ...inputStyle,
              appearance: "none",
              MozAppearance: "none",
              WebkitAppearance: "none",
            }}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          <FaChevronDown
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: 12,
              color: "#6b7280",
            }}
          />
          <span style={labelStyle}>År</span>
        </div>

        {/* Månad-filter */}
        <div style={boxStyle}>
          <select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            style={{
              ...inputStyle,
              appearance: "none",
              MozAppearance: "none",
              WebkitAppearance: "none",
            }}
          >
            {monthOptions.map((month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>

          <FaChevronDown
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: 12,
              color: "#6b7280",
            }}
          />
          <span style={labelStyle}>Månad</span>
        </div>

        {/* Exportera-knapp */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <button
            onClick={handleExport}
            style={{
              ...toolbarButtonStyle,
              background: "#2563eb",
              border: "1px solid #1d4ed8",
            }}
          >
            Exportera
          </button>
        </div>
      </div>

      {/* Rubrik med summering */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: "16px 18px",
          marginBottom: 14,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            color: "#111827",
          }}
        >
          Projektöversikt {!loading && `(${filteredRows.length})`}
        </h1>

        <div
          style={{
            display: "flex",
            gap: 28,
            flexWrap: "wrap",
            marginTop: 12,
            color: "#374151",
            fontSize: 14,
          }}
        >
          <div>
            <strong>Totalt antal pass:</strong> {totals.totalShifts}
          </div>
          <div>
            <strong>Totala timmar:</strong> {formatHours(totals.totalHours, true)}
          </div>
        </div>
      </div>

      {/* Projekt-tabell */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        {/* Tabellhuvud */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.5fr 1fr 1fr 1.5fr",
            gap: 12,
            padding: "14px 16px",
            borderBottom: "1px solid #e5e7eb",
            background: "#f9fafb",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>PROJEKT</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>TOTALT (TIMMAR)</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>ANTAL PASS</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>KUND</div>
        </div>

        {/* Tabellrader */}
        {loading ? (
          <LoadingIndicator message="Laddar projektöversikt..." />
        ) : filteredRows.length > 0 ? (
          filteredRows.map((row, index) => {
            return (
              <div
                key={`${row.project}-${index}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.5fr 1fr 1fr 1.5fr",
                  gap: 12,
                  alignItems: "center",
                  padding: "14px 16px",
                  borderBottom: "1px solid #f1f5f9",
                  background: "#ffffff",
                  borderLeft: "4px solid transparent",
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#111827",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.project}
                </div>

                <div
                  style={{
                    fontSize: 14,
                    color: "#374151",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatHours(row.totalHours)}
                </div>

                <div
                  style={{
                    fontSize: 14,
                    color: "#374151",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.shiftsCount}
                </div>

                <div
                  style={{
                    fontSize: 14,
                    color: "#374151",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.customerName || "-"}
                </div>
              </div>
            );
          })
        ) : (
          <div
            style={{
              padding: 24,
              textAlign: "center",
              color: "#6b7280",
              fontWeight: 500,
            }}
          >
            Inga projekt hittades.
          </div>
        )}
      </div>
    </div>
  );
}
